import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { axiosGetV3, axiosPost } from '../../api/axios-handler';

import { TagStatus, Availability } from './useCalls';
import { PeerDetail } from './usePeers';
import { ProviderType } from './useExternalProviderPlans';

export const experiencesQueryKeys = {
  recommended: ['recommendedExperiences'] as const,
  infiniteRecommended: ['infinitieExperiences'] as const,
  randomVideoExperiences: ['randomVideoExperiences'] as const,
};

export type PlaybackLocation = 'peer-profile' | 'search-results' | 'video-carousel';

export type Pathway = 'content' | 'resources' | 'people' | 'null' | 'default' | 'provider_plan' | 'faq';

export interface SearchProxyResponse {
  allowed_listener_tags: number[];
  blocked_users: number[];
  client_id: number;
  client_resources: Resource[];
  content: Content[];
  crisis_scores: Crisis;
  faq_result: string;
  global_resources: Resource[];
  ignore_search_query: boolean;
  min_score: number;
  pathway: PathwayResponse;
  people: People[];
  pool_sizes: PoolSizes;
  provider_plans: ProviderPlan[];
  query: string;
  test_user: boolean;
  unscored_client_resources: UnscoredResource[];
  unscored_content: UnscoredContent[];
  unscored_global_resources: UnscoredResource[];
  unscored_people: UnscoredPeople[];
  unscored_provider_plans: UnscoredProviderPlan[];
}

export interface Crisis {
  emergency: number;
  selfharm: number;
}

export interface PathwayResponse {
  content: number;
  faq: number;
  pathway: Pathway;
  people: number;
  provider_plan: number;
  reasoning: string;
  resources: number;
}

export interface Content {
  ai_summary?: string;
  ai_title?: string;
  admin_summary: string;
  area_ids: number[];
  audio_url: string;
  background_tags: string[];
  client_id: number;
  client_image_url: string;
  client_name?: string | null;
  duration: number;
  excerpt?: string;
  file_path: string;
  id: number;
  is_available: boolean;
  is_favorite: boolean;
  is_listener: boolean;
  listener_role_id: number;
  name: string;
  peer_type: string;
  profile_photo_square_file_url: string;
  rating: number;
  resource_client_id: number;
  resource_id: number;
  resource_image_url: string;
  score: number;
  similarity_score: number;
  tag_group_names: string[];
  tag_names: string[];
  title: string;
  transcription: string;
  video_id: string;
  vimeo_thumbnail_url?: string;
  media_type?: 'AUDIO' | 'VIDEO';
  match_reason?: 'name' | 'tag' | 'bio' | 'story' | 'related' | 'title' | 'summary' | 'transcript';
  match_snippet?: string;
  match_strength?: 'strong' | 'related';
  matched_story_title?: string;
}

export interface UnscoredContent {
  _collection_name: string;
  _id: string;
  admin_summary: any;
  ai_summary: any;
  created_at: string;
  duration: number;
  excerpt: any;
  file_url: string;
  id: number;
  listener_role: ListenerRole;
  listener_role_id: number;
  message_quality: number;
  opensearch_tag_ids?: number[];
  score: number;
  title: string;
  topic_tag_ids: number[];
  total_favorites: number;
  total_playbacks: number;
  transcription: string;
  video_id: any;
}

export interface Resource {
  challenge_areas: number[];
  client_id: number;
  description: string;
  id: number;
  image_url?: string;
  indexed_at: any;
  long_description: string;
  meta_keywords: string;
  name: string;
  notes: string;
  phone_number: string;
  sort_weight: number;
  status: string;
  subject_areas: number[];
  tag_names: string[];
  text_number?: string;
  url: string;
  url_description: string;
  wide_image_url: any;
  match_reason?: 'name' | 'tag' | 'bio' | 'story' | 'related' | 'title' | 'summary' | 'transcript';
  match_snippet?: string;
  match_strength?: 'strong' | 'related';
  matched_story_title?: string;
}

export interface UnscoredResource {
  _collection_name: string;
  _id: string;
  challenge_areas: any[];
  client_id: number;
  description: string;
  id: number;
  image_url: string;
  indexed_at: any;
  long_description: string;
  meta_keywords: string;
  name: string;
  notes: string;
  phone_number: string;
  score: number;
  sort_weight: number;
  status: string;
  subject_areas: any[];
  tag_names: string[];
  text_number: any;
  url: string;
  url_description: any;
  wide_image_url: any;
}
export interface People {
  about_me: string;
  can_take_calls: boolean;
  client_id?: number | null;
  client_name?: string | null;
  group_name: any;
  is_available: boolean;
  is_listener: boolean;
  is_peer_active: boolean;
  listener_role_id: number;
  profile_photo_url_square: string;
  score: number;
  state: string;
  user: User;
  listener_audio_tags?: string[];
  listener_audio_tag_groups?: string[];
  chatbot_id?: number | null;
  availabilities?: Availability[];
  match_reason?: 'name' | 'tag' | 'bio' | 'story' | 'related' | 'title' | 'summary' | 'transcript';
  match_snippet?: string;
  match_strength?: 'strong' | 'related';
  matched_story_title?: string;
}

export interface User {
  display_name: string;
  id: number;
  test_user: boolean;
}

export interface UnscoredPeople {
  _collection_name: string;
  _id: string;
  about_me: string;
  id: number;
  listener_role_id: number;
  name: string;
  score: number;
  state: string;
}

export interface PoolSizes {
  content: number;
  people: number;
  provider_plans: number;
  resources: number;
}

export interface ProviderPlan {
  common_uses: string;
  description: string;
  key: string;
  score: number;
  title: string;
}

export interface UnscoredProviderPlan {
  common_uses: string;
  description: string;
  key: ProviderType;
  score: number;
  title: string;
}

export interface RecommendedExperienceResponse {
  count: number;
  data: Experience[];
}

export interface Experience {
  admin_summary: string;
  ai_summary?: string;
  ai_title?: string;
  area_ids: number[];
  audio_url: string;
  background_tags: string[];
  client_id?: number;
  client_image_url?: string;
  client_name?: string | null;
  duration: number;
  excerpt: string;
  id: number;
  is_available: boolean;
  // DEPRECATED, sort of, in favor of experience rating
  is_favorite: boolean;
  is_listener: boolean;
  listener_audio_id: number;
  listener_role_id: number;
  name: string;
  peer_type: string;
  profile_photo_square_file_url: string;
  rating: number;
  resource_client_id?: number;
  resource_id?: number;
  resource_image_url?: string;
  score: number;
  similarity_score: number;
  title: string;
  transcription: string;
  tag_group_names?: string[];
  tag_names?: string[];
  video_id?: string;
  vimeo_thumbnail_url?: string;
}

export interface FavoriteExperience {
  created_at: string;
  duration: number;
  excerpt?: string;
  file_url: string;
  first_approved_at?: string;
  id: number;
  is_favorite: boolean;
  last_updated_at: string;
  listener_role: ListenerRole;
  listener_role_id: number;
  message_quality: number;
  title: string;
  topic_tags: FavoriteExperiencesTopicTag[];
}

export interface ListenerRole {
  about_me?: string;
  available: boolean;
  available_now: boolean;
  background_traits: any[];
  can_take_calls: boolean;
  connection_blocked: boolean;
  favorite_id?: number;
  id: number;
  is_active: boolean;
  is_peer_active: boolean;
  military_tag: any;
  next_available_text?: string;
  peer_status: string;
  primary_language_tag: string;
  profile_photo_file_url: string;
  profile_photo_square_file_url: string;
  profile_traits: string[];
  secondary_language_tag: string;
  share_link_url: string;
  spirituality_tag: string;
  state: string;
  status: string;
  tier_id: number;
  type: any;
  user: User;
}

export interface User {
  display_name: string;
  first_name: string;
  pronoun?: string | null;
}

export interface FavoriteExperiencesTopicTag {
  created_at: string;
  id: number;
  status: TagStatus;
  listener_audio_id: number;
  media: any;
  postcall_metadata_id: any;
  sort_order: number;
  tag: Tag;
  tag_id: number;
  user_id: any;
}

export interface Tag {
  id: number;
  is_background: boolean;
  is_default: boolean;
  is_required: boolean;
  is_visible: boolean;
  media?: Medum[];
  name: string;
  sort_order: number;
  tag_type: string;
}

export interface Medum {
  file_key: string;
  file_url: string;
}

export const useRateExperience = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    ({ rating, audioId, listenerId }: { rating: number; audioId: number; listenerId: number }) => {
      return axiosPost(
        `/listener_audio/${audioId}/favorite`,
        {
          rating,
        },
        'v3',
      );
    },
    {
      onSuccess: (_, { listenerId }) => {
        queryClient.invalidateQueries(['peerDetail', listenerId]);
        queryClient.invalidateQueries(['textSearchExperiences']);
      },
      onMutate: ({ rating, listenerId, audioId }) => {
        const updateRating = <T extends { id: number }>(item: T): T =>
          item.id === audioId ? { ...item, rating } : item;

        const updateContentRating = (item: Content): Content => updateRating(item);

        queryClient.setQueryData<RecommendedExperienceResponse>(['textSearchExperiences'], (data) => {
          if (!data) return;
          return {
            ...data,
            data: { ...data.data, ...data.data.map(updateRating) },
          };
        });

        const updateSearchProxyContent = (data: SearchProxyResponse | undefined) => {
          if (!data?.content) return data;
          return { ...data, content: data.content.map(updateContentRating) };
        };

        queryClient.setQueryData<SearchProxyResponse>(['textSearchExperiences'], updateSearchProxyContent);
        queryClient.setQueriesData<SearchProxyResponse>(
          { queryKey: ['textSearchExperiences'] },
          updateSearchProxyContent,
        );

        queryClient.setQueryData<import('./useContentCollection').LandingPageCollectionsResponse>(
          ['landingPageCollections'],
          (data) => {
            if (!data) return;
            const updateCollection = (collection: import('./useContentCollection').ContentCollection) => ({
              ...collection,
              items: collection.items.map((item) => {
                const itemData = item.data as Content;
                return itemData?.id === audioId ? { ...item, data: { ...itemData, rating } } : item;
              }),
            });
            return {
              topic_collections: (data.topic_collections || []).map(updateCollection),
              vertical_collections: (data.vertical_collections || []).map(updateCollection),
            };
          },
        );

        queryClient.setQueriesData<import('./useContentCollection').ContentCollection>(
          { queryKey: ['contentCollection'] },
          (data) => {
            if (!data) return data;
            return {
              ...data,
              items: data.items.map((item) => {
                const itemData = item.data as Content;
                return itemData?.id === audioId ? { ...item, data: { ...itemData, rating } } : item;
              }),
            };
          },
        );

        queryClient.setQueryData<PeerDetail>(['peerDetail', listenerId], (data) => {
          if (!data) return;
          return { ...data, listener_audio: data.listener_audio.map(updateRating) };
        });
      },
    },
  );

  return {
    ...mutation,
  };
};
